import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styles from "./Home.module.css";
import LazyImage from "./LazyImage";
import Seo, { createBreadcrumbList, createCollectionPageSchema } from "./Seo";
import SiteShell from "./SiteShell";
import seoHubs from "./seoHubs.json";
import { getImagePagePath } from "./imageRoutes";
import {
  createAlternateLinks,
  getBrowserLocale,
  getCategoryPath,
  getLocalizedPath,
  localizeCategory,
  localizeImage,
  translate,
} from "./i18n";

const EMPTY_LIST = [];

function getHubTranslation(hub, locale) {
  return hub.translations?.[locale] || hub.translations?.de;
}

function getHubPath(hub, locale) {
  const translation = getHubTranslation(hub, locale);
  return getLocalizedPath(`/themen/${translation.slug}`, locale);
}

function localizeHub(hub, locale) {
  const translation = getHubTranslation(hub, locale);

  return {
    ...hub,
    ...translation,
    path: getHubPath(hub, locale),
  };
}

function findHub(slug, locale) {
  return seoHubs.find((hub) =>
    Object.entries(hub.translations || {}).some(
      ([code, translation]) => code === locale && translation.slug === slug
    )
  ) || seoHubs.find((hub) =>
    Object.values(hub.translations || {}).some((translation) => translation.slug === slug)
  );
}

function TopicHub({ forcedLocale }) {
  const { hubSlug } = useParams();
  const locale = forcedLocale || getBrowserLocale();
  const t = (key, values) => translate(locale, key, values);
  const localPath = (targetPath) => getLocalizedPath(targetPath, locale);
  const rawHub = findHub(hubSlug, locale);
  const hub = rawHub ? localizeHub(rawHub, locale) : null;
  const [data, setData] = useState({ categories: [], images: [] });
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const response = await fetch("/db.json", {
          cache: "no-store",
          signal: controller.signal,
        });
        const json = await response.json();
        setData(json);
        setStatus("ready");
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        setStatus("error");
      }
    };

    fetchData();

    return () => controller.abort();
  }, []);

  const rawCategories = data.categories || EMPTY_LIST;
  const rawImages = data.images || EMPTY_LIST;
  const matchingTags = useMemo(
    () => new Set(rawHub?.categoryTags || []),
    [rawHub]
  );
  const categories = useMemo(
    () =>
      rawCategories
        .filter((category) => !category.inactive && matchingTags.has(category.tag))
        .map((category) => localizeCategory(category, locale)),
    [locale, matchingTags, rawCategories]
  );
  const images = useMemo(
    () =>
      rawImages
        .filter((image) => matchingTags.has(image.tag))
        .map((image) => localizeImage(image, locale)),
    [locale, matchingTags, rawImages]
  );
  const categoryNames = useMemo(
    () =>
      rawCategories
        .map((category) => localizeCategory(category, locale))
        .reduce((lookup, category) => {
          lookup[category.tag] = category.name;
          return lookup;
      }, {}),
    [locale, rawCategories]
  );
  const categoryCounts = useMemo(
    () =>
      images.reduce((counts, image) => {
        counts[image.tag] = (counts[image.tag] || 0) + 1;
        return counts;
      }, {}),
    [images]
  );
  const pdfCategories = useMemo(
    () => categories.filter((category) => category.pdfUrl),
    [categories]
  );

  const pageTitle = hub
    ? `${hub.title} | usmalbilder.ch`
    : `Thema nicht gefunden | usmalbilder.ch`;
  const pageDescription = hub?.description || t("freeColoringPages");
  const pagePath = hub?.path || localPath(`/themen/${hubSlug || ""}`);
  const alternateLinks = rawHub
    ? createAlternateLinks((targetLocale) => {
        const translation = getHubTranslation(rawHub, targetLocale);
        return getLocalizedPath(`/themen/${translation.slug}`, targetLocale);
      })
    : [];

  return (
    <SiteShell locale={locale}>
      <Seo
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        noindex={!hub}
        alternates={alternateLinks}
        structuredData={
          hub
            ? [
                createCollectionPageSchema({
                  title: pageTitle,
                  description: pageDescription,
                  path: pagePath,
                  items: images,
                  getItemUrl: (item) => getImagePagePath(item, locale),
                }),
                createBreadcrumbList([
                  { name: t("homeTitle"), path: localPath("/") },
                  { name: hub.title, path: pagePath },
                ]),
              ]
            : []
        }
      />

      {!hub && (
        <section className={styles.emptyState}>
          <h1 className={styles.emptyTitle}>{t("notFoundTitle")}</h1>
          <p className={styles.emptyText}>{t("notFoundText")}</p>
        </section>
      )}

      {hub && (
        <section className={styles.galleryOnlyLayout}>
          <section className={styles.seoPanel}>
            <h1 className={styles.galleryTitle}>{hub.title}</h1>
            <p>{hub.intro}</p>
            <p>{hub.description}</p>
          </section>

          <section className={styles.topicGrid}>
            <h2 className={styles.sectionTitle}>{t("categoriesTitle")}</h2>
            <div className={styles.topicCards}>
              {categories.map((category) => (
                <Link
                  className={styles.topicCard}
                  key={category.tag}
                  to={getCategoryPath(category, locale)}
                >
                  <span className={styles.topicTitle}>{category.name}</span>
                  <span className={styles.topicText}>{category.description}</span>
                  <span className={styles.topicCount}>
                    {t("topicCount", { count: categoryCounts[category.tag] || 0 })}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {pdfCategories.length > 0 && (
            <section className={styles.topicGrid}>
              <h2 className={styles.sectionTitle}>{t("pdfPacksTitle")}</h2>
              <p className={styles.sectionIntro}>{t("pdfPacksText")}</p>
              <div className={styles.topicCards}>
                {pdfCategories.map((category) => (
                  <a
                    className={styles.topicCard}
                    download
                    href={`/${category.pdfUrl}`}
                    key={category.tag}
                  >
                    <span className={styles.topicTitle}>{category.name}</span>
                    <span className={styles.topicText}>{category.description}</span>
                    <span className={styles.topicCount}>
                      {t("topicCount", {
                        count:
                          category.pdfImageCount ||
                          categoryCounts[category.tag] ||
                          0,
                      })}
                    </span>
                    <span className={styles.cardCta}>{t("downloadPdfPack")}</span>
                  </a>
                ))}
              </div>
            </section>
          )}

          <section className={styles.galleryPanel}>
            <div className={styles.galleryHeader}>
              <h2 className={styles.galleryTitle}>{t("popularImages")}</h2>
              <div className={styles.galleryCountPill}>
                {t("total", { count: images.length })}
              </div>
            </div>

            {status === "error" && (
              <div className={styles.errorCard}>
                <h3 className={styles.errorTitle}>{t("galleryErrorTitle")}</h3>
                <p className={styles.errorText}>{t("galleryErrorText")}</p>
              </div>
            )}

            {status === "ready" && (
              <div className={styles.galleryGrid}>
                {images.slice(0, 48).map((image) => (
                  <article className={styles.galleryCard} key={image.downloadUrl}>
                    <Link className={styles.galleryLink} to={getImagePagePath(image, locale)}>
                      <div className={styles.galleryMedia}>
                        <LazyImage
                          alt={image.description}
                          className={styles.colorPrintImage}
                          src={`/${image.thumbnailUrl}`}
                          title={image.title}
                        />
                      </div>
                      <div className={styles.galleryBody}>
                        <span className={styles.galleryTag}>
                          {categoryNames[image.tag] || image.tag}
                        </span>
                        <h3 className={styles.cardTitle}>{image.title}</h3>
                        <p className={styles.cardDescription}>{image.description}</p>
                        <span className={styles.cardCta}>{t("openPreview")}</span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </section>
        </section>
      )}
    </SiteShell>
  );
}

export default TopicHub;
